import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './pages/auth/LoginPage';
// Temporalmente comentadas hasta resolver problemas de TypeScript
import { PatientsPage } from './pages/crud/PatientsPage';
import { ProfessionalsPage } from './pages/crud/ProfessionalsPage';
import { SpecialtiesPage } from './pages/crud/SpecialtiesPage';
import { TreatmentsPage } from './pages/crud/TreatmentsPage';
import { AppointmentsPage } from './pages/crud/AppointmentsPage';
import './utils/i18n';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { StatisticsPage } from './pages/dashboard/StatisticsPage';

// Configuración de i18n
import './utils/i18n';

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Componente temporal para páginas en desarrollo
const ComingSoon = ({ pageName }: { pageName: string }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
        {pageName}
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Página en desarrollo - Próximamente disponible
      </p>
    </div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Ruta de login sin protección */}
                <Route path="/login" element={<LoginPage />} />
                
                {/* Rutas protegidas */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="statistics" element={<StatisticsPage />} />
                  <Route path="patients" element={<PatientsPage />} />
                  <Route path="professionals" element={<ProfessionalsPage />} />
                  <Route path="specialties" element={<SpecialtiesPage />} />
                  <Route path="treatments" element={<TreatmentsPage />} />
                  <Route path="appointments" element={<AppointmentsPage />} />
                </Route>
              </Routes>
              
              {/* Toast notifications */}
              <Toaster
                position="top-right"
                reverseOrder={false}
                gutter={8}
                containerClassName=""
                containerStyle={{}}
                toastOptions={{
                  // Default options for all toasts
                  duration: 3000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  },
                  // Success toast styles
                  success: {
                    style: {
                      background: '#10b981',
                      color: '#ffffff',
                    },
                    iconTheme: {
                      primary: '#ffffff',
                      secondary: '#10b981',
                    },
                  },
                  // Error toast styles
                  error: {
                    style: {
                      background: '#ef4444',
                      color: '#ffffff',
                    },
                    iconTheme: {
                      primary: '#ffffff',
                      secondary: '#ef4444',
                    },
                  },
                  // Loading toast styles
                  loading: {
                    style: {
                      background: '#3b82f6',
                      color: '#ffffff',
                    },
                    iconTheme: {
                      primary: '#ffffff',
                      secondary: '#3b82f6',
                    },
                  },
                }}
              />
            </div>
            <ReactQueryDevtools initialIsOpen={false} />
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
